import React, { useCallback } from "react";
import { AbsoluteFill, continueRender, delayRender } from "remotion";

import { IRenderParams, Overlay, OverlayType } from "@/components/editor/types";
import { SortedOutlines } from "@/components/editor/components/selection/sorted-outlines";
import { Layer } from "@/components/editor/components/core/layer";
import { mainpulateOverlays } from "@/components/editor/hooks/use-overlays";
import { preloadFonts } from "@/components/editor/components/overlays/text/text-font-family";

/**
 * Props for the Main component
 */
export type MainProps = {
  /** Array of overlay objects to be rendered */
  readonly overlays: Overlay[];
  /** Function to set the currently selected overlay ID */
  readonly setSelectedOverlayId: React.Dispatch<
    React.SetStateAction<number | null>
  >;
  /** Function to set the currently active panel type */
  readonly setActivePanel: (type: OverlayType) => void;
  /** Function to set the panel state open/close */
  readonly setSidebarIsOpen: (open: boolean) => void;

  /** Currently selected overlay ID, or null if none selected */
  readonly selectedOverlayId: number | null;
  /**
   * Function to update an overlay
   * @param overlayId - The ID of the overlay to update
   * @param updater - Function that receives the current overlay and returns an updated version
   */
  readonly changeOverlay: (
    overlayId: number,
    updater: (overlay: Overlay) => Overlay
  ) => void;
  /** Duration in frames of the composition */
  readonly durationInFrames: number;
  /** Frames per second of the composition */
  readonly fps: number;
  /** Width of the composition */
  readonly width: number;
  /** Height of the composition */
  readonly height: number;
  /** Background color of the composition */
  readonly backgroundColor: string;
  /** Render Params (Optional) */
  /** Array of render parameters for the overlays */
  readonly renderParams?: IRenderParams[];
  
  /** Function to set the editor state */
  readonly isEditor?: boolean;
};

const outer: React.CSSProperties = {
  backgroundColor: "#111827",
};

const layerContainer: React.CSSProperties = {
  overflow: "hidden",
};

/**
 * Main component that renders a canvas-like area with overlays and their outlines.
 * Handles selection of overlays and provides a container for editing them.
 *
 * @param props - Component props of type MainProps
 * @returns React component that displays overlays and their interactive outlines
 */
export const Main: React.FC<MainProps> = ({
  overlays,
  setSelectedOverlayId,
  selectedOverlayId,
  changeOverlay,
  backgroundColor,
  setActivePanel,
  setSidebarIsOpen,
  renderParams,
  isEditor = true
}) => {
  const [finalOverlays, setFinalOverlays] = React.useState<Overlay[]>(overlays);

  const waitForFont = delayRender();
  preloadFonts().then(() => {
    continueRender(waitForFont);
  });
  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0) {
        return;
      }

      setSelectedOverlayId(null);
    },
    [setSelectedOverlayId]
  );

  React.useEffect(() => {
    setFinalOverlays(()=> mainpulateOverlays(overlays,renderParams));
  }, [overlays,renderParams]);

  return (
    <AbsoluteFill
      style={{
        ...outer,
        backgroundColor: backgroundColor,
      }}
      onPointerDown={onPointerDown}
    >
      <AbsoluteFill style={layerContainer}>
        {overlays.map((overlay) => {
          return (
            <Layer
              key={overlay.id}
              overlay={overlay}
              selectedOverlayId={selectedOverlayId}
            />
          );
        })}
      </AbsoluteFill>
      {isEditor && (
      <SortedOutlines
        selectedOverlayId={selectedOverlayId}
        overlays={finalOverlays}
        setSelectedOverlayId={setSelectedOverlayId}
        changeOverlay={changeOverlay}
        setActivePanel={setActivePanel}
        setSidebarIsOpen={setSidebarIsOpen}
      />
      )}
    </AbsoluteFill>
  );
};
